import { SVGIcon } from '@/types';
import { StepConfig } from '@/steps/types';

/**
 *  Available store mutation for the footer.
 */
export enum FooterMutation {
    /** Used to update the floating action. */
    FLOATING_ACTION = 'footer:floatingAction',

    /** Used to update the info */
    INFO = 'footer:info',

    /** Used to update the settings of the next button. */
    NEXT = 'footer:next',
}

/**
 *  Holding the details of the footer's floating action.
 */
export interface FooterFloatingAction {
    /** Details of the SVG to render in the floating action button. */
    icon: SVGIcon;

    /** Method to invoke on tap. */
    action: VoidFunction;

    /**
     *
     *  Holding the background color of the floating action.
     *
     *  (i) Defaulting to blue light if undefined
     *
     */
    backgroundColor?: string | null;
}

/**
 *
 *  Holding the details of the footer's info.
 *
 *  (i) Automatically set through the step's meta data
 *
 */
export interface FooterInfo {
    /**
     *
     *  The default content of the information
     *
     *  (i) Shown within the info modal on smaller screens
     *
     */
    content: string;

    /** The text that is shown on the smaller screens. */
    small: string;
}

/**
 *  Holding the settings of the footer's next button.
 */
export interface FooterNext {
    /** The step to which the button navigates. */
    step?: StepConfig;

    /**
     *
     *  The title of the button.
     *
     *  (i) The step's title is used by default.
     *
     */
    title?: string;

    /**
     *
     *  Specific content for the button.
     *
     *  (!) Must be provided if no step is provided.
     *
     *  (i) By default, the content is a combination
     *  of the step's label and action. This is done
     *  so the action can be hidden on smaller screens.
     *
     */
    content?: string;

    /**
     *  Holding the disabled state of the button.
     */
    disabled: boolean;

    /**
     *  Holding the loading state of the footer.
     */
    loading: boolean;

    /**
     *
     *  The action to invoke on click.
     *
     *  (i) Doesn't get invoked when disabled by default.
     *
     */
    action: VoidFunction;
}

/**
 *  Holding the store's footer state.
 */
export interface FooterState {
    /**
     *
     *  Holding the details of the footer's floating action.
     *
     *  (i) Shown on smaller devices
     *
     */
    floatingAction: FooterFloatingAction | null;

    /**
     *  Holding the information to show.
     */
    info: FooterInfo | null;

    /**
     *  Holding the settings of the footer's next button.
     */
    next: FooterNext | null;
}
